import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { AnimatePresence } from "framer-motion";
import { HiMenu, HiX } from "react-icons/hi";
import { PORTFOLIO_DATA } from "../../data/constants.js";
import useScrollTracking from "../../hooks/useScrollTracking.js";
import { smoothScrollTo } from "../../utils/smoothScroll.js";
import logoImage from "../../assets/header-logo.png";
import ThemeToggle from "../ui/ThemeToggle.jsx";
import { useTheme } from "../../contexts/ThemeContext.jsx";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Get sections from navigation
  const sections = PORTFOLIO_DATA.navigation.map((item) =>
    item.href.replace("#", "")
  );

  const { activeSection, isScrolled } = useScrollTracking(sections);

  // Determine if we're on the hero section (home)
  // Use scroll position to determine if we're on hero - if not scrolled, we're on hero
  const isOnHeroSection = !isScrolled;

  // Show full header only when not on hero section
  const showFullHeader = !isOnHeroSection;

  // Smooth scroll to section
  const scrollToSection = (href) => {
    smoothScrollTo(href, { offset: 80 }); // Account for fixed header
    setIsMenuOpen(false);
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        !event.target.closest(".mobile-menu") &&
        !event.target.closest(".menu-button")
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen]);

  // Force header to stay fixed
  useEffect(() => {
    const forceFixed = () => {
      const header = document.querySelector('header[role="banner"]');
      if (header) {
        header.style.position = "fixed";
        header.style.top = "0";
        header.style.left = "0";
        header.style.right = "0";
        header.style.zIndex = "9999";
        header.style.width = "100%";
      }
    };

    forceFixed();
    const interval = setInterval(forceFixed, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        showFullHeader
          ? "bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg"
          : "bg-transparent dark:bg-gray-900/95 dark:backdrop-blur-md"
      }`}
      role="banner"
      style={{
        position: "fixed !important",
        top: "0 !important",
        left: "0 !important",
        right: "0 !important",
        zIndex: "9999 !important",
        width: "100% !important",
        transform: "translateZ(0)",
      }}
    >
      <nav
        id="main-navigation"
        className="max-w-6xl 3xl:max-w-none mx-auto px-6 sm:px-8 md:px-10 lg:px-12 3xl:px-16"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Mobile menu button - moved to far left */}
          <div className="md:hidden order-1">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              className={`menu-button inline-flex items-center justify-center p-4 rounded-md transition-colors duration-300 touch-manipulation min-w-[48px] min-h-[48px] ${
                showFullHeader
                  ? "text-gray-700 dark:text-gray-300 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                  : "text-gray-500 dark:text-white hover:text-white dark:hover:text-blue-300 hover:bg-white/10 dark:hover:bg-gray-800/30"
              }`}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation-menu"
              aria-label={
                isMenuOpen ? "Close navigation menu" : "Open navigation menu"
              }
              type="button"
            >
              <span className="sr-only">
                {isMenuOpen ? "Close main menu" : "Open main menu"}
              </span>
              {isMenuOpen ? (
                <HiX
                  className="block h-6 w-6 pointer-events-none"
                  aria-hidden="true"
                />
              ) : (
                <HiMenu
                  className="block h-6 w-6 pointer-events-none"
                  aria-hidden="true"
                />
              )}
            </motion.button>
          </div>

          {/* Logo/Name - centered on mobile - only shown when not on hero section or when scrolled */}
          {showFullHeader ? (
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex-shrink-0 order-2 md:order-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <button
                type="button"
                onClick={() => scrollToSection("#home")}
                className="flex items-center gap-3 text-xl lg:text-2xl font-bold transition-colors duration-300 text-gray-900 dark:text-white"
                aria-label="Go to home section"
              >
                <motion.img
                  src={logoImage}
                  alt="Ed Lima Logo"
                  className="w-10 h-10 lg:w-12 lg:h-12 rounded-full object-cover border-2 border-white dark:border-white shadow-sm"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  onError={(e) => {
                    console.error("❌ HEADER logo failed to load:", logoImage);
                    e.target.style.display = "none";
                  }}
                  onLoad={() => {
                    // Logo loaded successfully
                  }}
                />
                {PORTFOLIO_DATA.personal.name}
              </button>
            </motion.div>
          ) : (
            <div className="flex-shrink-0 order-2 md:order-1" />
          )}

          {/* Desktop Navigation */}
          <div className="hidden md:block order-3 md:order-2">
            <div className="ml-10 flex items-baseline space-x-4 lg:space-x-8">
              {PORTFOLIO_DATA.navigation.map((item) => {
                // Skip "Home" when on hero section
                if (item.name === "Home" && isOnHeroSection) {
                  return null;
                }

                return (
                  <motion.button
                    key={item.name}
                    onClick={() => scrollToSection(item.href)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-3 py-2 rounded-md text-sm lg:text-base font-medium transition-all duration-300 relative ${
                      activeSection === item.href.replace("#", "")
                        ? showFullHeader
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-500 dark:text-white"
                        : showFullHeader
                        ? "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                        : "text-gray-500 dark:text-white hover:text-white dark:hover:text-blue-300"
                    }`}
                  >
                    {item.name}
                    {activeSection === item.href.replace("#", "") &&
                      showFullHeader && (
                        <motion.div
                          layoutId="activeSection"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 30,
                          }}
                        />
                      )}
                  </motion.button>
                );
              })}

              {/* Theme Toggle */}
              <div
                className={`ml-4 pl-4 border-l ${
                  showFullHeader
                    ? "border-gray-300 dark:border-gray-600"
                    : "border-gray-400/50 dark:border-gray-500/50"
                }`}
              >
                <ThemeToggle isOnHeroSection={isOnHeroSection} />
              </div>
            </div>
          </div>

          {/* Mobile: Theme Toggle */}
          <div className="md:hidden order-3">
            <ThemeToggle isOnHeroSection={isOnHeroSection} />
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mobile-menu fixed left-0 right-0 top-16 z-[9998] px-4"
            >
              <div
                id="mobile-navigation-menu"
                className="px-2 pt-2 pb-3 space-y-2 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-lg mt-2 shadow-lg border border-gray-200 dark:border-gray-700"
                role="menu"
                aria-label="Mobile navigation menu"
              >
                {PORTFOLIO_DATA.navigation.map((item) => {
                  // Skip "Home" when on hero section
                  if (item.name === "Home" && isOnHeroSection) {
                    return null;
                  }

                  return (
                    <motion.button
                      key={item.name}
                      onClick={() => scrollToSection(item.href)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`block w-full text-left px-4 py-4 rounded-md text-base font-medium transition-colors duration-300 touch-manipulation ${
                        activeSection === item.href.replace("#", "")
                          ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30"
                          : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                      aria-label={`Navigate to ${item.name} section`}
                    >
                      {item.name}
                    </motion.button>
                  );
                })}

                {/* Mobile Theme Toggle */}
                <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600 mt-2 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Theme
                    </span>
                    <ThemeToggle isOnHeroSection={isOnHeroSection} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
